import React from 'react';
import { ComparisonPeriod, MetricFormatterFn } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { getFormattedMetricDateLabel } from 'src/components/BeaconV2Components/SplineChart/SplineChart';
import PromotionHoverTooltip from 'src/components/BeaconV2Components/HoverTooltip/PromotionHoverTooltip';
import { promotionTypeToDisplayName } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/constants';
import { StacklineTheme } from '@stackline/ui';
import { ValueOf } from 'sl-api-connector';
import { Point } from 'highcharts';
import { WeeklyPromotionData } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/hooks';

interface PromotionHoverCardProps {
  primaryPromotionData: ValueOf<WeeklyPromotionData>;
  primaryPoint: Point;
  secondaryPoint: Point;
  percentChange: number;
  weekId: number;
  metricType: ValueOf<typeof METRICTYPE>;
  isProductEntity: boolean;
  theme: StacklineTheme;
  formatMetric: MetricFormatterFn;
  comparisonPeriod: string;
}

const PromotionHoverCard = ({
  primaryPromotionData,
  primaryPoint,
  secondaryPoint,
  percentChange,
  weekId,
  metricType,
  isProductEntity,
  theme,
  formatMetric,
  comparisonPeriod
}: PromotionHoverCardProps) => {
  const promotionPrice = primaryPromotionData?.retailPrice_avg_value ?? 0;
  const percentDiscount = primaryPromotionData?.averagePromotionDiscount_computed_value ?? 0;

  // Only used for entity levels other than product
  const promotionCount = primaryPromotionData?.stacklineSku_cardinality_count;

  return (
    // Render the normal metrics above the promotion metrics
    <PromotionHoverTooltip
      title={`Week ${weekId % 100}`}
      primaryColor={(primaryPoint.color as string) || theme.colors.primary}
      primaryMetric={formatMetric(primaryPoint.y, metricType, {
        decimalPlaces: 2,
        showFullValue: metricType === METRICTYPE.PERCENT,
        showNegative: true
      })}
      primaryMetricLabel={getFormattedMetricDateLabel(
        weekId,
        undefined,
        primaryPoint.series.index === 2 && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR // the secondary series will always have index 2, subtract a year if comparing against prior year
      )}
      change={
        percentChange !== undefined
          ? formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })
          : undefined
      } // get rid of negative sign if there is one
      rawChange={percentChange}
      secondaryMetric={
        secondaryPoint
          ? formatMetric(secondaryPoint.y, metricType, {
              decimalPlaces: 2,
              showFullValue: metricType === METRICTYPE.PERCENT,
              showNegative: true
            })
          : ''
      }
      secondaryMetricLabel={getFormattedMetricDateLabel(
        weekId,
        undefined,
        comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
      )}
      secondaryColor={secondaryPoint ? (secondaryPoint.color as string) : theme.colors.primaryGray}
      // Metrics for the promotion portion of the tooltip
      promotionHoverTooltipOptions={{
        promotionTitle: isProductEntity
          ? promotionTypeToDisplayName[primaryPromotionData?.promoType] ?? 'Promotion'
          : `${formatMetric(promotionCount, METRICTYPE.VOLUME, { showFullValue: false })} Promotions`,
        promotionPrimaryMetric: formatMetric(promotionPrice, METRICTYPE.MONEY),
        promotionFormattedChange:
          percentDiscount !== undefined
            ? formatMetric(Math.abs(percentDiscount), METRICTYPE.PERCENT, { decimalPlaces: 2 })
            : undefined,

        promotionRawChange: percentDiscount,
        // Render additional promotion data for the tooltip when at other entity levels
        renderAdditionalPromotionData: isProductEntity
          ? undefined
          : () => {
              return (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    marginTop: '8px'
                  }}
                >
                  <span
                    style={{
                      color: theme.colors.secondary,
                      fontFamily: 'Roboto',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    AVG DISCOUNT
                  </span>
                  <span
                    style={{
                      color: theme.colors.primary,
                      fontFamily: 'Roboto',
                      fontSize: '12px',
                      fontWeight: '500',
                      marginTop: '4px'
                    }}
                  >
                    {percentDiscount !== undefined
                      ? formatMetric(Math.abs(percentDiscount), METRICTYPE.PERCENT, { decimalPlaces: 2 })
                      : undefined}
                  </span>
                </div>
              );
            }
      }}
      theme={theme}
    />
  );
};

export default PromotionHoverCard;
